import { ImageIcon, Maximize2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ScreenshotDialog } from "@/components/agents/configuration-server/ScreenshotDialog";
import type { ScreenshotItem } from "@/components/agents/conversation/types";
import { useRunLogScreenshot } from "@/hooks/useConfigMigration";
import { useObjectUrl } from "@/hooks/useObjectUrl";

/** Largura da miniatura no desenho do Claude Design (a altura da imagem é 136px). */
const THUMBNAIL_WIDTH_PX = 240;

/** Quanto antes de entrar na tela o print já pede a imagem. */
const NEAR_VIEWPORT = "600px";

/** `true` quando o elemento está perto da área visível; sem IntersectionObserver, sempre. */
function useNearViewport<T extends Element>() {
  const ref = useRef<T>(null);
  const [near, setNear] = useState(() => typeof IntersectionObserver === "undefined");
  useEffect(() => {
    const element = ref.current;
    if (near || !element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setNear(true);
      },
      { rootMargin: NEAR_VIEWPORT },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [near]);
  return { ref, near };
}

/**
 * Print de um passo da conversa, em miniatura, servido pela rota de print do log do
 * run. Todos os prints do histórico aparecem como imagem: a miniatura pede a imagem
 * quando chega perto da tela, e o terminal antecipa só os mais recentes em segundo
 * plano (`usePrefetchScreenshots`). A miniatura e o modal usam a mesma consulta
 * (`useRunLogScreenshot`), então abrir a imagem inteira não baixa de novo.
 */
export function ScreenshotThumbnail({
  projectId,
  runId,
  item,
  position,
}: {
  projectId: string;
  runId: string;
  item: ScreenshotItem;
  /** Qual print é, entre os da conversa: o rótulo diz "Print 2 de 5" ao leitor de tela. */
  position?: { index: number; total: number };
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const label = position
    ? t("Screenshot {{index}} of {{total}}", position)
    : t("View screenshot");
  const { ref, near } = useNearViewport<HTMLDivElement>();

  return (
    <div
      ref={ref}
      data-testid={`conversation-screenshot-${item.seq}`}
      className="mb-0.5 ml-10 mt-1.5 self-start"
    >
      <ScreenshotPreview
        projectId={projectId}
        runId={runId}
        logId={item.logId}
        enabled={near}
        label={label}
        onOpen={() => setOpen(true)}
      />
      {open && (
        <ScreenshotDialog
          projectId={projectId}
          runId={runId}
          logId={item.logId}
          screenshot={item.screenshot}
          message={
            item.screenshot.kind === "error"
              ? t("Screen captured after a SAP error.")
              : t("Screen captured during the conversation.")
          }
          sapMessage={item.sapMessage ?? undefined}
          createdAt={item.createdAt}
          title={item.screenshot.kind === "error" ? t("SAP screen error") : t("SAP screen")}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function ScreenshotPreview({
  projectId,
  runId,
  logId,
  enabled,
  label,
  onOpen,
}: {
  projectId: string;
  runId: string;
  logId: number;
  enabled: boolean;
  label: string;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const query = useRunLogScreenshot(projectId, runId, logId, enabled);
  const url = useObjectUrl(query.data);
  const state: "loading" | "loaded" | "error" = query.isError
    ? "error"
    : url
      ? "loaded"
      : "loading";

  return (
    <button
      type="button"
      aria-label={label}
      onClick={onOpen}
      data-testid="chat-screenshot"
      style={{ width: THUMBNAIL_WIDTH_PX }}
      className="relative block max-w-full cursor-zoom-in overflow-hidden rounded-lg border border-[rgba(255,255,255,0.15)] p-0 text-left transition-colors hover:border-[rgba(255,200,61,0.6)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--exed-gold)]"
    >
      {state === "error" ? (
        <span
          role="alert"
          data-testid="chat-screenshot-area"
          className="flex h-[136px] w-full items-center bg-[#101a2e] px-3 text-left text-sm text-[#ff9b9b]"
        >
          {t("Could not load the screenshot.")}
        </span>
      ) : state === "loaded" ? (
        <img
          src={url ?? undefined}
          alt={label}
          data-testid="chat-screenshot-area"
          className="block h-[136px] w-full object-cover object-left-top"
        />
      ) : (
        <span
          role="status"
          data-testid="chat-screenshot-area"
          className="flex h-[136px] w-full items-center justify-center bg-[#101a2e]"
        >
          <ImageIcon aria-hidden="true" className="h-5 w-5 text-[#5f7ea8]" />
          <span className="sr-only">{t("Loading screenshot…")}</span>
        </span>
      )}
      <span className="flex items-center gap-1.5 bg-[var(--exed-navy)] px-2.5 py-1.5 font-sans text-[12px] text-[rgba(255,255,255,0.8)]">
        <Maximize2 aria-hidden="true" className="h-3 w-3 shrink-0" />
        <span className="min-w-0 truncate">{label}</span>
      </span>
    </button>
  );
}
